import { ViewIcon, ViewOffSlashIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Stack, Typography, TextField, Button, Box, IconButton, InputAdornment, Divider, LinearProgress } from "@mui/material";
import { useAppDispatch } from "@/hooks";
import useSnackbar from "@/hooks/useSnackbar";
import { register } from "../../auth/slice";
import GoogleIcon from "@/assets/icons/google-icon.svg";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import routes from "@/constants/routes";
import { auth } from "@/firebase";
import PasswordValidator from "@/components/password/PasswordValidator";
import PasswordMatching from "@/components/password/PasswordMatching";
import { useLazyFetchUserByEmailQuery } from "@/api/usersApi";

const signUpSchema = Yup.object().shape({
  name: Yup.string().min(2).max(30).required("Campo requerido"),
  lastname: Yup.string().min(2).max(30).required("Campo requerido"),
  email: Yup.string().email("Correo no válido").required("Campo requerido"),
  password: Yup.string()
    .min(8, "Mínimo 8 caracteres")
    .matches(/\d/, "Debe incluir un número")
    .matches(/[A-Z]/, "Debe incluir una mayúscula")
    .matches(/[a-z]/, "Debe incluir una minúscula")
    .required("Campo requerido"),
  repeatPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Las contraseñas no coinciden")
    .required("Campo requerido"),
});

const SignUpForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [openSnackbar] = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [fetchExistingUser] = useLazyFetchUserByEmailQuery();

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      setIsRedirecting(true);
      navigate("/login");
    } catch (error) {
      console.error("Google sign-in error:", error);
      openSnackbar("Error al iniciar con Google", "error");
    }
  };

  const formik = useFormik({
    initialValues: { name: "", lastname: "", email: "", password: "", repeatPassword: "" },
    validationSchema: signUpSchema,
    onSubmit: async ({ name, lastname, email, password }) => {
      if (isRedirecting) return;

      try {
        const existingUser = await fetchExistingUser(email);

        if (existingUser.data) {
          openSnackbar("Este correo ya está registrado.", "warning");
          return;
        }

        const result = await dispatch(register({ name, lastname, email, password })).unwrap();

        if (result) {
          setIsRedirecting(true);

          await new Promise((resolve) => setTimeout(resolve, 800));

          navigate(routes.login, {
            state: {
              email,
            },
          });
        }
      } catch (error: any) {
        console.error("Registration error:", error);
        setIsRedirecting(false);
        if (error.code === "permission-denied") {
          openSnackbar("No se pudo crear tu perfil. Inténtalo de nuevo.", "error");
        } else {
          openSnackbar("Hubo un error creando tu cuenta", "error");
        }
      }
    },
  });

  return (
    <Box>
      {isRedirecting && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Redirigiendo a login...
          </Typography>
          <LinearProgress sx={{ mt: 1 }} />
        </Box>
      )}
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <Typography variant="h1">Crear Cuenta</Typography>

          <Stack direction={"row"} spacing={2}>
            <TextField
              fullWidth
              name="name"
              label="Nombre"
              margin="normal"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={!!formik.touched.name && !!formik.errors.name}
              helperText={formik.touched.name && formik.errors.name}
              onBlur={formik.handleBlur}
            />
            <TextField
              fullWidth
              name="lastname"
              label="Apellido"
              margin="normal"
              value={formik.values.lastname}
              onChange={formik.handleChange}
              error={!!formik.touched.lastname && !!formik.errors.lastname}
              helperText={formik.touched.lastname && formik.errors.lastname}
              onBlur={formik.handleBlur}
            />
          </Stack>

          <TextField
            fullWidth
            name="email"
            type="email"
            label="Correo"
            margin="normal"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={!!formik.touched.email && !!formik.errors.email}
            helperText={formik.touched.email && formik.errors.email}
            onBlur={formik.handleBlur}
          />

          <TextField
            fullWidth
            name="password"
            type={showPassword ? "text" : "password"}
            label="Contraseña"
            margin="normal"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={!!formik.touched.password && !!formik.errors.password}
            helperText={formik.touched.password && formik.errors.password}
            onBlur={formik.handleBlur}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <HugeiconsIcon icon={ViewOffSlashIcon} /> : <HugeiconsIcon icon={ViewIcon} />}</IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <TextField
            fullWidth
            name="repeatPassword"
            type={showPassword ? "text" : "password"}
            label="Repetir Contraseña"
            margin="normal"
            value={formik.values.repeatPassword}
            onChange={formik.handleChange}
            error={!!formik.touched.repeatPassword && !!formik.errors.repeatPassword}
            helperText={formik.touched.repeatPassword && formik.errors.repeatPassword}
            onBlur={formik.handleBlur}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <HugeiconsIcon icon={ViewOffSlashIcon} /> : <HugeiconsIcon icon={ViewIcon} />}</IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {formik.values.password && formik.values.repeatPassword ? <PasswordMatching password={formik.values.password} repeatPassword={formik.values.repeatPassword} /> : null}

          <PasswordValidator password={formik.values.password} />

          <Button size="large" variant="contained" type="submit" disabled={!formik.isValid || !formik.dirty || isRedirecting}>
            {isRedirecting ? "Creando cuenta..." : "Crear Cuenta"}
          </Button>

          <Divider>ó</Divider>

          <Button
            fullWidth
            size="large"
            onClick={signInWithGoogle}
            startIcon={<img src={GoogleIcon} alt="Google" />}
            sx={{
              color: "black",
              background: "linear-gradient(180deg, #ECF5FF 0%, #E4EFFF 100%)",
            }}
            disabled={isRedirecting}
          >
            Ingresar con Google
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default SignUpForm;
